import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import ExpenseFormModal from '../components/ExpenseFormModal';
import CommissionFormModal from '../components/CommissionFormModal';
import ProductCostLedgerModal from '../components/ProductCostLedgerModal';
import InvoiceExpenseFormModal from '../components/InvoiceExpenseFormModal';
import { generateInvoiceExpensePdf } from '../components/invoiceExpensePdf';

const PAGE_SIZE = 10;

const ExpensesTab = () => {
  const [view, setView] = useState('expenses'); // 'expenses' | 'commissions' | 'invoice_expenses'

  const [expenses, setExpenses] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [invoiceExpenses, setInvoiceExpenses] = useState([]);
  const [summary, setSummary] = useState({
    general_total: 0, product_total: 0, commission_total: 0,
    general_count: 0, product_count: 0, commission_count: 0, grand_total: 0,
  });
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [showInvoiceExpense, setShowInvoiceExpense] = useState(false);

  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('2025-01-01');
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showCostLedger, setShowCostLedger] = useState(false);

  // For expandable invoice expense rows
  const [expandedRow, setExpandedRow] = useState(null); // sale_item_id
  const [expandedDetail, setExpandedDetail] = useState(null); // { detail, expenses, summary }
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [view, scopeFilter, typeFilter, modeFilter, fromDate, toDate, debouncedSearch]);

  useEffect(() => {
    chemicalService.getExpenseTypes({ page: 1, limit: 500 })
      .then((res) => setExpenseTypes(res.data || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const summaryParams = {};
      if (fromDate) summaryParams.from_date = fromDate;
      if (toDate) summaryParams.to_date = toDate;

      if (view === 'expenses') {
        const params = { page, limit: PAGE_SIZE };
        if (scopeFilter !== 'all') params.scope = scopeFilter;
        if (typeFilter !== 'all') params.expense_type_id = typeFilter;
        if (modeFilter !== 'all') params.payment_mode = modeFilter;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        const [listRes, summaryRes] = await Promise.all([
          chemicalService.getExpenses(params),
          chemicalService.getExpensesSummary(summaryParams).catch(() => ({ data: summary })),
        ]);
        setExpenses(listRes.data || []);
        setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
        setSummary(summaryRes.data || summary);
      } else if (view === 'commissions') {
        const params = { page, limit: PAGE_SIZE };
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        const [listRes, summaryRes] = await Promise.all([
          chemicalService.getCommissions(params),
          chemicalService.getExpensesSummary(summaryParams).catch(() => ({ data: summary })),
        ]);
        setCommissions(listRes.data || []);
        setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
        setSummary(summaryRes.data || summary);
      } else if (view === 'invoice_expenses') {
        const params = { page, limit: PAGE_SIZE };
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;

        const [listRes, summaryRes] = await Promise.all([
          chemicalService.getGroupedInvoiceExpenses(params),
          chemicalService.getExpensesSummary(summaryParams).catch(() => ({ data: summary })),
        ]);
        setInvoiceExpenses(listRes.data || []);
        setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
        setSummary(summaryRes.data || summary);
        setExpandedRow(null);
        setExpandedDetail(null);
      }
    } catch (err) {
      console.error('Error fetching:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, page, scopeFilter, typeFilter, modeFilter, fromDate, toDate, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddClick = () => { setEditingExpense(null); setShowFormModal(true); };
  const handleEditClick = (exp) => { setEditingExpense(exp); setShowFormModal(true); };

  const handleDelete = async (exp) => {
    if (!window.confirm(`Delete this expense (Rs ${formatNumber(exp.amount)})?\n\nCannot be undone.`)) return;
    try {
      await chemicalService.deleteExpense(exp.id);
      alert('✅ Expense deleted.');
      if (expenses.length === 1 && page > 1) setPage(page - 1); else fetchData();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };

  const handleDeleteCommission = async (c) => {
    if (!window.confirm(`Delete commission for ${c.agent_name} (Rs ${formatNumber(c.amount)})?\n\nCannot be undone.`)) return;
    try {
      await chemicalService.deleteCommission(c.id);
      alert('✅ Commission deleted.');
      if (commissions.length === 1 && page > 1) setPage(page - 1); else fetchData();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };

  const handleSaved = () => { setShowFormModal(false); setEditingExpense(null); fetchData(); };
  const handleCommissionSaved = () => { setShowCommissionModal(false); fetchData(); };
  const clearDateFilters = () => {
  setFromDate('2025-01-01');
  setToDate(new Date().toISOString().split('T')[0]);
};

  // Expand/collapse invoice expense row
  const toggleExpand = async (saleItemId) => {
    if (expandedRow === saleItemId) {
      setExpandedRow(null);
      setExpandedDetail(null);
      return;
    }
    try {
      setLoadingExpand(true);
      setExpandedRow(saleItemId);
      const res = await chemicalService.getSaleLineDetail(saleItemId);
      setExpandedDetail(res.data);
    } catch (err) {
      console.error('Error expanding:', err);
      setExpandedRow(null);
      alert('❌ Failed to load details');
    } finally {
      setLoadingExpand(false);
    }
  };

  // Print invoice expense PDF
  const handlePrintInvoiceExpense = async (saleItemId) => {
    try {
      setPrintingId(saleItemId);
      const res = await chemicalService.getSaleLineDetail(saleItemId);
      const { detail, expenses: exps, summary: sum } = res.data;
      generateInvoiceExpensePdf(detail, exps, sum);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to generate PDF'));
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ─── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3498db' }}>
          <div style={styles.summaryLabel}>General Expenses</div>
          <div style={{ ...styles.summaryValue, color: '#3498db' }}>{formatLakh(summary.general_total)}</div>
          <div style={styles.summarySub}>{summary.general_count} entries</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #9b59b6' }}>
          <div style={styles.summaryLabel}>Product-specific</div>
          <div style={{ ...styles.summaryValue, color: '#9b59b6' }}>{formatLakh(summary.product_total)}</div>
          <div style={styles.summarySub}>{summary.product_count} entries</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e67e22' }}>
          <div style={styles.summaryLabel}>Commissions</div>
          <div style={{ ...styles.summaryValue, color: '#e67e22' }}>{formatLakh(summary.commission_total)}</div>
          <div style={styles.summarySub}>{summary.commission_count} entries</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Grand Total</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>{formatLakh(summary.grand_total)}</div>
          <div style={styles.summarySub}>all expenditure</div>
        </div>
      </div>

      {/* ─── View toggle (3 tabs now) ─── */}
      <div style={styles.viewTabs}>
        {[
          { id: 'expenses', label: '💸 Expenses' },
          { id: 'commissions', label: '🤝 Commissions' },
          { id: 'invoice_expenses', label: '🧾 Invoice Expenses' },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            style={{
              ...styles.viewTab,
              background: view === v.id ? '#e74c3c' : 'white',
              color: view === v.id ? 'white' : '#2c3e50',
              borderColor: view === v.id ? '#e74c3c' : '#e0e0e0',
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* ─── Filters ─── */}
      <div style={styles.toolbar}>
        {view === 'expenses' && (
          <>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Scope:</label>
              <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} style={styles.filterSelect}>
                <option value="all">All</option>
                <option value="general">General</option>
                <option value="product">Product-specific</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Type:</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={styles.filterSelect}>
                <option value="all">All Types</option>
                {expenseTypes.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Mode:</label>
              <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} style={styles.filterSelect}>
                <option value="all">All Modes</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
          </>
        )}

        <div style={styles.dateRangeWrap}>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={styles.dateInput} />
          <span style={{ color: '#95a5a6', fontSize: '13px' }}>→</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={styles.dateInput} />
          {(fromDate || toDate) && <button onClick={clearDateFilters} style={styles.clearButton}>✕</button>}
        </div>

        <div style={styles.toolbarRight}>
          {view !== 'invoice_expenses' && (
            <div style={styles.searchWrap}>
              <span style={styles.searchIcon}>⌕</span>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={view === 'expenses' ? 'Search description, paid to/by...' : 'Search agent, invoice...'} style={styles.searchInput} />
            </div>
          )}
          <button onClick={() => setShowCostLedger(true)} style={{ ...styles.commissionButton, background: '#9b59b6' }}>
            📊 Cost Ledger
          </button>
          <button onClick={() => setShowInvoiceExpense(true)} style={{ ...styles.commissionButton, background: '#16a085' }}>
            🧾 Invoice Expense
          </button>
          {view === 'expenses' && (
            <button onClick={handleAddClick} style={styles.addButton}>+ New Expense</button>
          )}
          {view === 'commissions' && (
            <button onClick={() => setShowCommissionModal(true)} style={styles.commissionButton}>+ New Commission</button>
          )}
        </div>
      </div>

      {/* ─── Expenses Table ─── */}
      {view === 'expenses' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Scope</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Paid By</th>
                <th style={styles.th}>Paid To</th>
                <th style={styles.th}>Mode</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" style={styles.noData}><div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>Loading expenses...</td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan="9" style={styles.noData}><div style={{ fontSize: '40px', marginBottom: '8px' }}>💸</div>No expenses match your filters</td></tr>
              ) : (
                expenses.map((exp) => (
                  <tr key={exp.id} style={styles.row}>
                    <td style={styles.td}>{formatDate(exp.expense_date)}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.typeBadge, background: (exp.expense_type_color || '#7f8c8d') + '20', color: exp.expense_type_color || '#7f8c8d', border: `1px solid ${(exp.expense_type_color || '#7f8c8d')}40` }}>
                        ● {exp.expense_type_name}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {exp.scope === 'product' ? (
                        <span style={styles.productBadge}>📦 {exp.product_code || exp.product_name || 'Product'}</span>
                      ) : (
                        <span style={styles.generalBadge}>🏢 General</span>
                      )}
                    </td>
                    <td style={styles.td}>{exp.description || <span style={styles.dash}>—</span>}</td>
                    <td style={styles.td}>{exp.paid_by || <span style={styles.dash}>—</span>}</td>
                    <td style={styles.td}>{exp.paid_to || <span style={styles.dash}>—</span>}</td>
                    <td style={styles.td}>
                      <span style={styles.modeBadge}>{formatPaymentMode(exp.payment_mode)}</span>
                      {exp.payment_mode === 'cheque' && exp.cheque_number && (
                        <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 2 }}>{exp.bank_name} · {exp.cheque_number}</div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <strong style={{ color: '#e74c3c', fontSize: '15px' }}>− Rs {formatNumber(exp.amount)}</strong>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button onClick={() => handleEditClick(exp)} style={styles.editButton} title="Edit">✏️</button>
                        <button onClick={() => handleDelete(exp)} style={styles.deleteButton} title="Delete">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Commissions Table ─── */}
      {view === 'commissions' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Agent</th>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Invoice</th>
                <th style={styles.th}>Customer</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Base</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>%</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Commission</th>
                <th style={styles.th}>Mode</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" style={styles.noData}><div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>Loading commissions...</td></tr>
              ) : commissions.length === 0 ? (
                <tr><td colSpan="10" style={styles.noData}><div style={{ fontSize: '40px', marginBottom: '8px' }}>🤝</div>No commissions yet — click "+ New Commission"</td></tr>
              ) : (
                commissions.map((c) => (
                  <tr key={c.id} style={styles.row}>
                    <td style={styles.td}>{formatDate(c.date)}</td>
                    <td style={styles.td}><strong>{c.agent_name}</strong></td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{c.product_name}</div>
                      <div style={{ fontSize: 11, color: '#95a5a6' }}>{c.product_code}</div>
                    </td>
                    <td style={styles.td}><span style={styles.invoiceBadge}>{c.invoice_no}</span></td>
                    <td style={styles.td}>{c.customer_name || <span style={styles.dash}>—</span>}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>Rs {formatNumber(c.base_amount)}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{c.percent != null ? `${parseFloat(c.percent)}%` : '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <strong style={{ color: '#e67e22', fontSize: '15px' }}>Rs {formatNumber(c.amount)}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.modeBadge}>{formatPaymentMode(c.payment_mode)}</span>
                      {c.payment_mode === 'cheque' && c.cheque_number && (
                        <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 2 }}>{c.bank_name} · {c.cheque_number}</div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button onClick={() => handleDeleteCommission(c)} style={styles.deleteButton} title="Delete">🗑️</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Invoice Expenses Table (3rd toggle) ─── */}
      {view === 'invoice_expenses' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Invoice</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Product</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Sale Amt</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total Expenses</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Margin</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Items</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" style={styles.noData}><div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>Loading invoice expenses...</td></tr>
              ) : invoiceExpenses.length === 0 ? (
                <tr><td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>🧾</div>
                  No invoice expenses yet — click "🧾 Invoice Expense" to record some
                </td></tr>
              ) : (
                invoiceExpenses.map((ie) => {
                  const isExpanded = expandedRow === ie.sale_item_id;
                  const isPrinting = printingId === ie.sale_item_id;
                  return (
                    <React.Fragment key={ie.sale_item_id}>
                      <tr
                      style={{ ...styles.row, cursor: 'pointer', background: isExpanded ? '#f0faf5' : 'white' }}
                        onClick={() => toggleExpand(ie.sale_item_id)}
                      >
                        <td style={styles.td}>{formatDate(ie.invoice_date)}</td>
                        <td style={styles.td}><span style={styles.invoiceBadge}>{ie.invoice_no}</span></td>
                        <td style={styles.td}><strong>{ie.customer_name || '—'}</strong></td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 600 }}>{ie.product_name}</div>
                          <div style={{ fontSize: 11, color: '#95a5a6' }}>{ie.product_code}</div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>Rs {formatNumber(ie.line_amount)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <strong style={{ color: '#e74c3c' }}>Rs {formatNumber(ie.total_expenses)}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <strong style={{ color: parseFloat(ie.real_margin) >= 0 ? '#27ae60' : '#e74c3c' }}>
                            Rs {formatNumber(ie.real_margin)}
                          </strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={styles.countBadge}>{ie.line_count}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button
                              onClick={() => handlePrintInvoiceExpense(ie.sale_item_id)}
                              style={{ ...styles.printButton, opacity: isPrinting ? 0.6 : 1 }}
                              disabled={isPrinting}
                              title="Print PDF"
                            >
                              {isPrinting ? '⏳' : '🖨️'}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete all expenses for ${ie.invoice_no}?\n\nCannot be undone.`)) return;
                                try {
                                  await chemicalService.deleteAllInvoiceExpensesForSaleLine(ie.sale_item_id);
                                  fetchData();
                                } catch (err) {
                                  alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
                                }
                              }}
                              style={styles.deleteButton}
                              title="Delete all expenses for this invoice"
                            >🗑️</button>
                            
                          </div>
                        </td>
                      </tr>

                      {/* ─── Expanded detail ─── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="9" style={{ padding: 0, background: '#f8fdf9' }}>
                            {loadingExpand ? (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>⏳ Loading breakdown...</div>
                            ) : expandedDetail ? (
                              <div style={styles.expandedBox}>
                                <div style={styles.expandedGrid}>
                                  <div><span style={styles.expandLabel}>Qty</span>{fmtQty(expandedDetail.detail.quantity)} {expandedDetail.detail.unit}</div>
                                  <div><span style={styles.expandLabel}>Rate</span>{fmtMoney(expandedDetail.detail.rate)}</div>
                                  <div><span style={styles.expandLabel}>Total</span>{fmtMoney(expandedDetail.detail.line_amount)}</div>
                                  <div><span style={styles.expandLabel}>GST</span>{fmtMoney(expandedDetail.detail.line_gst)}</div>
                                  <div><span style={styles.expandLabel}>COGS</span>{fmtMoney(expandedDetail.detail.cogs)}</div>
                                </div>

                                {expandedDetail.summary.by_type && expandedDetail.summary.by_type.length > 0 && (
                                  <div style={styles.expandedBreakdown}>
                                    {expandedDetail.summary.by_type.map((t) => (
                                      <div key={t.expense_type_id} style={styles.breakdownRow}>
                                        <span style={{ color: t.expense_type_color || '#7f8c8d', fontWeight: 600 }}>
                                          ● {t.expense_type_name}
                                        </span>
                                        <span style={{ fontWeight: 700 }}>Rs {formatNumber(t.total)}</span>
                                      </div>
                                    ))}
                                    <div style={styles.breakdownTotal}>
                                      <span>Total Expenses</span>
                                      <span style={{ color: '#e74c3c' }}>Rs {formatNumber(expandedDetail.summary.grand_total)}</span>
                                    </div>
                                  </div>
                                )}

                                {expandedDetail.expenses && expandedDetail.expenses.length > 0 && (
                                  <div style={styles.expandedLines}>
                                   {expandedDetail.expenses.map((ex, i) => (
                                    <div key={i} style={styles.expandedLineRow}>
                                      <span style={{ fontSize: 12, color: '#95a5a6', minWidth: 90 }}>{ex.expense_type_name}</span>
                                      <span style={{ flex: 1, marginLeft: 10, fontSize: 13 }}>{ex.label}</span>
                                      <span style={{ fontWeight: 600, fontSize: 13 }}>Rs {formatNumber(ex.amount)}</span>
                                    </div>
                                  ))}
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Pagination ─── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} {view === 'invoice_expenses' ? 'invoices' : view}
          </div>
          <div style={styles.paginationButtons}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}>«</button>
            <button onClick={() => setPage(page - 1)} disabled={page === 1} style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}>‹ Prev</button>
            <span style={styles.pageIndicator}>Page {pagination.page} of {pagination.totalPages}</span>
            <button onClick={() => setPage(page + 1)} disabled={page >= pagination.totalPages} style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}>Next ›</button>
            <button onClick={() => setPage(pagination.totalPages)} disabled={page >= pagination.totalPages} style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}>»</button>
          </div>
        </div>
      )}

      {showFormModal && (
        <ExpenseFormModal expense={editingExpense} onClose={() => { setShowFormModal(false); setEditingExpense(null); }} onSaved={handleSaved} />
      )}
      {showCommissionModal && (
        <CommissionFormModal onClose={() => setShowCommissionModal(false)} onSaved={handleCommissionSaved} />
      )}
      {showCostLedger && (
        <ProductCostLedgerModal onClose={() => setShowCostLedger(false)} />
      )}
      {showInvoiceExpense && (
        <InvoiceExpenseFormModal
          onClose={() => setShowInvoiceExpense(false)}
          onSaved={() => { setShowInvoiceExpense(false); fetchData(); }}
        />
      )}

    </div>
  );
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatNumber = (n) => Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 1000000) return `Rs ${(num / 1000000).toFixed(2)}M`;
  return `Rs ${formatNumber(num)}`;
};
const formatPaymentMode = (m) => {
  const map = { cash: 'Cash', cheque: 'Cheque', bank_transfer: 'Bank Transfer', easypaisa: 'EasyPaisa', jazzcash: 'JazzCash' };
  return map[m] || m;
};
const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
const fmtQty = (n) => parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');

const styles = {
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' },
  summaryCard: { background: 'white', padding: '18px 22px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  summaryLabel: { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px', fontWeight: '600' },
  summaryValue: { fontSize: '26px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },
  viewTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  viewTab: { padding: '10px 20px', border: '2px solid', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  toolbar: { background: 'white', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', marginBottom: '16px' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '8px' },
  filterLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase' },
  filterSelect: { padding: '8px 10px', border: '2px solid #e0e0e0', borderRadius: '7px', fontSize: '13px', fontFamily: 'inherit' },
  dateRangeWrap: { display: 'flex', alignItems: 'center', gap: '6px' },
  dateInput: { padding: '9px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', color: '#2c3e50', background: 'white', cursor: 'pointer', outline: 'none', minWidth: '140px' },
  clearButton: { padding: '4px 10px', background: '#f0f0f0', color: '#7f8c8d', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' },
  searchWrap: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#95a5a6', fontSize: '16px' },
  searchInput: { padding: '10px 12px 10px 36px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', width: '260px', fontFamily: 'inherit' },
  addButton: { padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  commissionButton: { padding: '10px 20px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  tableWrap: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: { padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  dash: { color: '#bdc3c7', fontStyle: 'italic' },
  typeBadge: { padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  productBadge: { background: '#f3e5f5', color: '#6a1b9a', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  generalBadge: { background: '#e3f2fd', color: '#1565c0', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  invoiceBadge: { background: '#eafaf1', color: '#1e8449', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', fontFamily: 'monospace' },
  modeBadge: { background: '#f0f0f0', color: '#555', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' },
  countBadge: { background: '#e8f4fd', color: '#1565c0', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '700' },
  editButton: { padding: '6px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  deleteButton: { padding: '6px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  printButton: { padding: '6px 10px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  expandButton: { padding: '6px 10px', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' },
  expandedBox: { padding: '16px 24px' },
  expandedGrid: { display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '14px', fontSize: '14px', color: '#2c3e50' },
  expandLabel: { fontSize: '10px', fontWeight: '700', color: '#95a5a6', textTransform: 'uppercase', display: 'block', marginBottom: '2px' },
  expandedBreakdown: { background: '#fdf6ec', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', border: '1px solid #f5e0bf' },
  breakdownRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' },
  breakdownTotal: { display: 'flex', justifyContent: 'space-between', padding: '8px 0 2px', fontSize: '14px', fontWeight: '700', borderTop: '1px solid #f0d5a8', marginTop: '4px' },
  expandedLines: { background: '#f8f9fa', borderRadius: '8px', padding: '10px 14px' },
  expandedLineRow: { display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #eee' },
  paginationWrap: { marginTop: '16px', background: 'white', padding: '14px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: { padding: '8px 14px', background: 'white', color: '#2c3e50', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '0 12px', fontSize: '13px', color: '#2c3e50', fontWeight: '600' },
};

export default ExpensesTab;